import React, { useState, useEffect, useRef } from "react";
import {
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiUserCheck,
  FiUserX,
  FiMail,
  FiPhone,
  FiRefreshCw,
  FiX,
  FiCheck,
  FiAlertTriangle,
  FiArrowLeft,
  FiCalendar,
  FiMapPin,
  FiBriefcase,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiFileText
} from "react-icons/fi";
import api from "../../../connector/api";
import Pagination from "../reuse/Pagination";

// Safe string extraction utility
const extractString = (value) => {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    return value.position || value.title || value.name || value.first_name || value.name_en || "";
  }
  return "";
};

// Reusable Custom Dropdown Component
function CustomSelect({ options, value, onChange, placeholder = "Select option" }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value) || options[0];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-700 hover:bg-zinc-100/70 focus:outline-none transition-colors min-w-[110px]"
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <FiChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-full min-w-[130px] rounded-lg bg-white border border-zinc-200 shadow-lg py-1 z-30 animate-in fade-in zoom-in-95 duration-100">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-xs flex items-center justify-between transition-colors ${
                option.value === value
                  ? "bg-zinc-100 text-zinc-900 font-medium"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`}
            >
              <span>{option.label}</span>
              {option.value === value && <FiCheck className="w-3 h-3 text-zinc-700" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmployeeListTab() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedGender, setSelectedGender] = useState("all");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Active detail view employee state
  const [activeDetailEmployee, setActiveDetailEmployee] = useState(null);
  const [detailTab, setDetailTab] = useState("info"); // 'info' | 'attendance' | 'leaves'

  // Modal states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    gender: "Male",
    status: "Active",
    role: ""
  });

  const genderOptions = [
    { label: "All Genders", value: "all" },
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" }
  ];

  const statusOptions = [
    { label: "All Status", value: "all" },
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" }
  ];

  const pageSizeOptions = [
    { label: "5 / page", value: 5 },
    { label: "10 / page", value: 10 },
    { label: "20 / page", value: 20 },
    { label: "50 / page", value: 50 }
  ];

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("page_size", itemsPerPage);
      if (search) params.append("search", search);
      if (selectedStatus !== "all") params.append("status", selectedStatus);
      if (selectedGender !== "all") params.append("gender", selectedGender);

      const response = await api.get(`/employees/?${params.toString()}`);
      const data = response.data;

      if (Array.isArray(data)) {
        setEmployees(data);
        setTotalCount(data.length);
      } else {
        setEmployees(data.results || []);
        setTotalCount(data.count || 0);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentPage, itemsPerPage, selectedStatus, selectedGender]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchEmployees();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch full details (with attendance_records & leaves) when row is clicked
  const handleSelectDetail = async (emp) => {
    setDetailLoading(true);
    setActiveDetailEmployee(emp);
    try {
      const res = await api.get(`/employees/${emp.id}/`);
      setActiveDetailEmployee(res.data);
    } catch (err) {
      console.error("Failed to fetch complete employee details:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  // Open Form Modal for Create/Update
  const handleOpenFormModal = (employee = null) => {
    if (employee) {
      setSelectedEmployee(employee);
      const userObj = employee.user || {};
      setFormData({
        first_name: extractString(employee.first_name || userObj.first_name),
        last_name: extractString(employee.last_name || userObj.last_name),
        email: extractString(employee.email || userObj.email),
        phone: extractString(employee.phone || userObj.phone),
        gender: employee.gender || "Male",
        status: employee.status || "Active",
        role: extractString(employee.role || employee.position)
      });
    } else {
      setSelectedEmployee(null);
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender: "Male",
        status: "Active",
        role: ""
      });
    }
    setIsFormModalOpen(true);
  };

  // Submit Handler
  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedEmployee) {
        const res = await api.put(`/employees/${selectedEmployee.id}/`, formData);
        if (activeDetailEmployee?.id === selectedEmployee.id) {
          setActiveDetailEmployee({ ...activeDetailEmployee, ...res.data, ...formData });
        }
      } else {
        await api.post("/employees/", formData);
      }
      setIsFormModalOpen(false);
      fetchEmployees();
    } catch (error) {
      console.error("Error saving employee:", error);
    } finally {
      setSaving(false);
    }
  };

  // Delete Handler
  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    try {
      await api.delete(`/employees/${selectedEmployee.id}/`);
      setIsDeleteModalOpen(false);
      if (activeDetailEmployee?.id === selectedEmployee.id) {
        setActiveDetailEmployee(null);
      }
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      console.error("Error deleting employee:", error);
    } finally {
      setSaving(false);
    }
  };

  // Status Badge Helper
  const renderAttendanceBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "present") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <FiCheckCircle className="w-3 h-3" /> Present
        </span>
      );
    }
    if (s === "absent") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
          <FiXCircle className="w-3 h-3" /> Absent
        </span>
      );
    }
    if (s === "late") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          <FiClock className="w-3 h-3" /> Late
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-100 text-zinc-600 border border-zinc-200">
        <FiAlertCircle className="w-3 h-3" /> {status || "—"}
      </span>
    );
  };

  // ----------------------------------------------------
  // RENDER DETAILED VIEW
  // ----------------------------------------------------
  if (activeDetailEmployee) {
    const emp = activeDetailEmployee;
    const userObj = emp.user || {};
    const firstName = extractString(emp.first_name || userObj.first_name);
    const lastName = extractString(emp.last_name || userObj.last_name);
    const email = extractString(emp.email || userObj.email) || "—";
    const phone = extractString(emp.phone || userObj.phone) || "—";
    const roleText = extractString(emp.role) || extractString(emp.position) || "Staff";
    const departmentText = extractString(emp.department_name) || extractString(emp.department) || "General";
    const isActive = (emp.status || "").toLowerCase() === "active" || (emp.is_active ?? true);
    const avatarUrl = emp.profile_picture_url || emp.profile_picture;
    const address = emp.address || {};
    const attendanceRecords = emp.attendance_records || [];
    const leavesList = emp.leaves || [];

    return (
      <div className="space-y-4 animate-in fade-in duration-150">
        {/* Back Navigation Bar */}
        <div className="flex items-center justify-between border-b border-zinc-200/80 pb-3">
          <button
            onClick={() => setActiveDetailEmployee(null)}
            className="flex items-center gap-2 text-xs font-medium text-zinc-600 hover:text-zinc-900 transition-colors cursor-pointer"
          >
            <FiArrowLeft className="w-4 h-4" />
            <span>Back to Employee List</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleOpenFormModal(emp)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200/70 text-zinc-700 text-xs font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FiEdit2 className="w-3.5 h-3.5" />
              <span>Edit Details</span>
            </button>
            <button
              onClick={() => {
                setSelectedEmployee(emp);
                setIsDeleteModalOpen(true);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg transition-colors cursor-pointer"
            >
              <FiTrash2 className="w-3.5 h-3.5" />
              <span>Delete</span>
            </button>
          </div>
        </div>

        {/* Header Profile Card */}
        <div className="bg-white rounded-lg border border-zinc-200/80 p-6 shadow-2xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={`${firstName} ${lastName}`}
                  className="w-16 h-16 rounded-full object-cover border border-zinc-200/80 shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-zinc-100 border border-zinc-200/80 flex items-center justify-center font-bold text-zinc-700 text-xl shrink-0 uppercase">
                  {firstName[0] || ""}{lastName[0] || "E"}
                </div>
              )}
              <div className="space-y-1">
                <h2 className="text-lg font-bold text-zinc-900">
                  {lastName} {firstName}
                </h2>
                <p className="text-xs font-medium text-zinc-500">{roleText} • {departmentText}</p>
                <div className="pt-1 flex items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                      isActive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200/60"
                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                    }`}
                  >
                    {isActive ? <FiUserCheck className="w-3 h-3" /> : <FiUserX className="w-3 h-3" />}
                    {isActive ? "Active Employee" : "Inactive"}
                  </span>
                  <span className="text-[11px] text-zinc-400 font-mono">ID: #{emp.id}</span>
                </div>
              </div>
            </div>

            {detailLoading && (
              <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                <FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> Fetching records...
              </div>
            )}
          </div>

          {/* Tab Selection */}
          <div className="flex items-center gap-4 border-b border-zinc-100 pt-2 text-xs font-medium">
            <button
              onClick={() => setDetailTab("info")}
              className={`pb-2 transition-colors cursor-pointer border-b-2 ${
                detailTab === "info"
                  ? "border-zinc-900 text-zinc-900 font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              Employee Information
            </button>
            <button
              onClick={() => setDetailTab("attendance")}
              className={`pb-2 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 ${
                detailTab === "attendance"
                  ? "border-zinc-900 text-zinc-900 font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <FiCalendar className="w-3.5 h-3.5" />
              <span>Attendance Records</span>
              <span className="px-1.5 py-0.2 bg-zinc-100 text-zinc-600 rounded-full text-[10px]">
                {attendanceRecords.length}
              </span>
            </button>
            <button
              onClick={() => setDetailTab("leaves")}
              className={`pb-2 transition-colors cursor-pointer border-b-2 flex items-center gap-1.5 ${
                detailTab === "leaves"
                  ? "border-zinc-900 text-zinc-900 font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-800"
              }`}
            >
              <FiFileText className="w-3.5 h-3.5" />
              <span>Leave History</span>
              <span className="px-1.5 py-0.2 bg-zinc-100 text-zinc-600 rounded-full text-[10px]">
                {leavesList.length}
              </span>
            </button>
          </div>

          {/* Sub-Tab 1: General Info */}
          {detailTab === "info" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 text-xs">
              <div className="space-y-4">
                <h3 className="font-semibold text-zinc-800 text-xs border-b border-zinc-100 pb-1">
                  Contact & Personal Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-zinc-400 block text-[11px] mb-0.5">Email Address</span>
                    <span className="font-medium text-zinc-800 flex items-center gap-2">
                      <FiMail className="w-3.5 h-3.5 text-zinc-400" /> {email}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-400 block text-[11px] mb-0.5">Phone Number</span>
                    <span className="font-medium text-zinc-800 flex items-center gap-2">
                      <FiPhone className="w-3.5 h-3.5 text-zinc-400" /> {phone}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-400 block text-[11px] mb-0.5">Gender</span>
                    <span className="font-medium text-zinc-800">{emp.gender || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-zinc-800 text-xs border-b border-zinc-100 pb-1">
                  Position & Address
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-zinc-400 block text-[11px] mb-0.5">Role / Position</span>
                    <span className="font-medium text-zinc-800 flex items-center gap-2">
                      <FiBriefcase className="w-3.5 h-3.5 text-zinc-400" /> {roleText}
                    </span>
                  </div>
                  <div>
                    <span className="text-zinc-400 block text-[11px] mb-0.5">Address</span>
                    <span className="font-medium text-zinc-800 flex items-center gap-2">
                      <FiMapPin className="w-3.5 h-3.5 text-zinc-400" />
                      {address.street || address.city
                        ? `${address.street || ""} ${address.city || ""}`
                        : "No address recorded"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sub-Tab 2: Attendance Records Table */}
          {detailTab === "attendance" && (
            <div className="space-y-3 pt-1">
              <div className="border border-zinc-200/80 rounded-lg overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                      <th className="py-2.5 px-4">Date</th>
                      <th className="py-2.5 px-4">Status</th>
                      <th className="py-2.5 px-4">Comment</th>
                      <th className="py-2.5 px-4">Recorded By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
                    {attendanceRecords.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-zinc-400">
                          No attendance records found for this employee.
                        </td>
                      </tr>
                    ) : (
                      attendanceRecords.map((rec) => (
                        <tr key={rec.id} className="hover:bg-zinc-50/60 transition-colors">
                          <td className="py-2.5 px-4 font-mono text-[11px] text-zinc-800">{rec.date}</td>
                          <td className="py-2.5 px-4">{renderAttendanceBadge(rec.status)}</td>
                          <td className="py-2.5 px-4 text-zinc-500">{rec.comment || "—"}</td>
                          <td className="py-2.5 px-4 text-zinc-500">
                            {rec.recorded_by
                              ? `${rec.recorded_by.first_name || ""} ${rec.recorded_by.last_name || rec.recorded_by.username}`.trim()
                              : "System"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sub-Tab 3: Leaves Table */}
          {detailTab === "leaves" && (
            <div className="space-y-3 pt-1">
              <div className="border border-zinc-200/80 rounded-lg overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                      <th className="py-2.5 px-4">Type</th>
                      <th className="py-2.5 px-4">Start Date</th>
                      <th className="py-2.5 px-4">End Date</th>
                      <th className="py-2.5 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
                    {leavesList.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-zinc-400">
                          No leave requests found for this employee.
                        </td>
                      </tr>
                    ) : (
                      leavesList.map((leave, idx) => (
                        <tr key={leave.id || idx} className="hover:bg-zinc-50/60 transition-colors">
                          <td className="py-2.5 px-4 font-medium text-zinc-800">{leave.leave_type || "Leave"}</td>
                          <td className="py-2.5 px-4 font-mono text-[11px]">{leave.start_date || "—"}</td>
                          <td className="py-2.5 px-4 font-mono text-[11px]">{leave.end_date || "—"}</td>
                          <td className="py-2.5 px-4">
                            <span className="capitalize px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-100 text-zinc-700">
                              {leave.status || "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modals placed inside detail view scope to maintain accessibility */}
        {renderModals()}
      </div>
    );
  }

  // Helper render for shared Edit and Delete modals
  function renderModals() {
    return (
      <>
        {/* Create / Edit Form Modal */}
        {isFormModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
            <div className="bg-white rounded-xl border border-zinc-200 shadow-xl w-full max-w-lg overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
                <h3 className="font-semibold text-zinc-900 text-sm">
                  {selectedEmployee ? "Update Employee" : "Add New Employee"}
                </h3>
                <button
                  onClick={() => setIsFormModalOpen(false)}
                  className="p-1 text-zinc-400 hover:text-zinc-700 rounded-md transition-colors cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              <form onSubmit={handleSaveEmployee} className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">First Name</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Last Name</label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Gender</label>
                    <CustomSelect
                      options={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" }
                      ]}
                      value={formData.gender}
                      onChange={(val) => setFormData({ ...formData, gender: val })}
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Status</label>
                    <CustomSelect
                      options={[
                        { label: "Active", value: "Active" },
                        { label: "Inactive", value: "Inactive" }
                      ]}
                      value={formData.status}
                      onChange={(val) => setFormData({ ...formData, status: val })}
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-600 mb-1">Role / Position</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs focus:outline-none focus:border-zinc-400"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => setIsFormModalOpen(false)}
                    className="px-3 py-1.5 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-3 py-1.5 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                  >
                    {saving ? "Saving..." : selectedEmployee ? "Update Employee" : "Create Employee"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {isDeleteModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/40 backdrop-blur-xs animate-in fade-in duration-150">
            <div className="bg-white rounded-xl border border-zinc-200 shadow-xl w-full max-w-md p-5 space-y-4">
              <div className="flex items-center gap-3 text-red-600">
                <div className="p-2.5 bg-red-50 rounded-full border border-red-100">
                  <FiAlertTriangle className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-zinc-900 text-sm">Delete Employee</h3>
              </div>

              <p className="text-xs text-zinc-600 leading-relaxed">
                Are you sure you want to delete this employee? This action cannot be undone and will permanently remove their record from the database.
              </p>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-3 py-1.5 rounded-lg border border-zinc-200 text-xs font-medium text-zinc-600 hover:bg-zinc-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteEmployee}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                >
                  {saving ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ----------------------------------------------------
  // STANDARD MAIN LIST VIEW
  // ----------------------------------------------------
  return (
    <div className="space-y-4">
      {/* Top Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-1">
        <div className="flex flex-wrap flex-1 items-center gap-2 max-w-2xl">
          <div className="relative flex-1 min-w-[200px]">
            <FiSearch className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by name, email, or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-zinc-50 border border-zinc-200/80 rounded-lg text-xs text-zinc-800 focus:outline-none focus:border-zinc-400 transition-colors"
            />
          </div>

          <CustomSelect
            options={statusOptions}
            value={selectedStatus}
            onChange={(val) => {
              setSelectedStatus(val);
              setCurrentPage(1);
            }}
          />

          <CustomSelect
            options={genderOptions}
            value={selectedGender}
            onChange={(val) => {
              setSelectedGender(val);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handleOpenFormModal()}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 hover:bg-zinc-800 text-white text-xs font-medium rounded-lg transition-colors shadow-2xs cursor-pointer"
          >
            <FiPlus className="w-3.5 h-3.5" />
            <span>Add Employee</span>
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-zinc-200/80 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/70 border-b border-zinc-200/80 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                <th className="py-3 px-4">Employee</th>
                <th className="py-3 px-4">Department / Role</th>
                <th className="py-3 px-4">Contact</th>
                <th className="py-3 px-4">Gender</th>
                <th className="py-3 px-4">Status</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-xs text-zinc-700">
              {loading ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-zinc-400">
                    <FiRefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-zinc-400" />
                    Loading employees...
                  </td>
                </tr>
              ) : employees.length === 0 ? (
                <tr>
                  <td colSpan="6" className="py-12 text-center text-zinc-400">
                    No employees found matching criteria.
                  </td>
                </tr>
              ) : (
                employees.map((emp) => {
                  const userObj = emp.user || {};
                  const firstName = extractString(emp.first_name || userObj.first_name);
                  const lastName = extractString(emp.last_name || userObj.last_name);
                  const email = extractString(emp.email || userObj.email) || "—";
                  const phone = extractString(emp.phone || userObj.phone);
                  const roleText = extractString(emp.role) || extractString(emp.position) || "Staff";
                  const departmentText = extractString(emp.department_name) || extractString(emp.department) || "General";
                  const idNumber = emp.id_number || emp.employee_id || emp.id;
                  const isActive = (emp.status || "").toLowerCase() === "active" || (emp.is_active ?? true);
                  const avatarUrl = emp.profile_picture_url || emp.profile_picture;

                  return (
                    <tr
                      key={emp.id}
                      onClick={() => handleSelectDetail(emp)}
                      className="hover:bg-zinc-50 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={`${firstName} ${lastName}`}
                              className="w-8 h-8 rounded-full object-cover border border-zinc-200/80 shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200/80 flex items-center justify-center font-bold text-zinc-600 text-xs shrink-0 uppercase group-hover:bg-zinc-200/60 transition-colors">
                              {firstName[0] || ""}{lastName[0] || "E"}
                            </div>
                          )}
                          <div>
                            <span className="font-semibold text-zinc-900 group-hover:text-blue-600 transition-colors block">
                              {lastName}, {firstName}
                            </span>
                            <p className="text-[10px] text-zinc-400">ID: #{idNumber}</p>
                          </div>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        <p className="font-medium text-zinc-800">{roleText}</p>
                        <p className="text-[10px] text-zinc-400">{departmentText}</p>
                      </td>

                      <td className="py-3 px-4 space-y-0.5">
                        <div className="flex items-center gap-1.5 text-zinc-600 text-[11px]">
                          <FiMail className="w-3 h-3 text-zinc-400 shrink-0" />
                          <span className="truncate">{email}</span>
                        </div>
                        {phone && (
                          <div className="flex items-center gap-1.5 text-zinc-400 text-[10px]">
                            <FiPhone className="w-3 h-3 text-zinc-400 shrink-0" />
                            <span>{phone}</span>
                          </div>
                        )}
                      </td>

                      <td className="py-3 px-4 text-zinc-600">
                        {emp.gender || "—"}
                      </td>

                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                            isActive
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200/60"
                              : "bg-zinc-100 text-zinc-500 border-zinc-200"
                          }`}
                        >
                          {isActive ? <FiUserCheck className="w-3 h-3" /> : <FiUserX className="w-3 h-3" />}
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            title="Edit Employee"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenFormModal(emp);
                            }}
                            className="p-1.5 rounded-md hover:bg-zinc-200/70 text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
                          >
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Delete Employee"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedEmployee(emp);
                              setIsDeleteModalOpen(true);
                            }}
                            className="p-1.5 rounded-md hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors cursor-pointer"
                          >
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Footer Pagination */}
        <Pagination
          currentPage={currentPage}
          totalCount={totalCount}
          pageSize={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(newSize) => {
            setItemsPerPage(newSize);
            setCurrentPage(1);
          }}
          pageSizeOptions={pageSizeOptions}
        />
      </div>

      {renderModals()}
    </div>
  );
}